import React from 'react';
import { Box, Typography, Chip, Avatar, Card, CardContent, Stack } from '@mui/material';
import Comments from './Comments';
import EnergyNeeds from './EnergyNeeds';
import { grey, primary } from '../../styles/tokens/colors';

const DIETARY_TRANSLATIONS = {
  vegetarian: 'Vegetarien',
  vegan: 'Vegan',
  'gluten-free': 'Sans gluten',
  'dairy-free': 'Sans lactose',
  paleo: 'Paleo',
  keto: 'Keto',
  'high-protein': 'Hyperproteine',
  'low-carb': 'Low carb',
  other: 'Autre',
};

const INTOLERANCE_TRANSLATIONS = {
  gluten: 'Gluten',
  lactose: 'Lactose',
  nuts: 'Fruits a coque',
  shellfish: 'Crustaces',
  soy: 'Soja',
  eggs: 'Oeufs',
  fish: 'Poisson',
  pork: 'Porc',
  beef: 'Boeuf',
  chicken: 'Poulet',
  other: 'Autre',
};

const ACTIVITY_LABELS = {
  sedentary: 'Sedentaire',
  lightly_active: 'Peu actif',
  moderate: 'Modere',
  active: 'Actif',
  very_active: 'Tres actif',
};

/**
 * GeneralInfo - Profil client et besoins energetiques
 *
 * V4.0: Design System Alignment
 * - Utilise les tokens de couleurs
 * - Stats en grid responsive
 * - Avatar avec initiales
 *
 * Props:
 * - isClientView: Si true, masque le bloc nom/email (deja visible dans le header)
 */
const GeneralInfo = ({ user, dietaryPreferences, goals, allergies, planId, isClientView = false }) => {
  if (!user) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              minHeight: 100,
              alignItems: 'center',
            }}
          >
            <Typography color="text.secondary">Aucun utilisateur associe</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();

  // Determiner le label d'activite
  const getActivityLabel = () => {
    // Si activityLevel existe, l'utiliser
    if (user.activityLevel) {
      return ACTIVITY_LABELS[user.activityLevel] || user.activityLevel;
    }
    // Sinon, utiliser le NAP du sportProfile
    if (user.sportProfile?.nap) {
      const napLabels = {
        1.2: 'Sedentaire',
        1.4: 'Peu actif',
        1.5: 'Leger',
        1.6: 'Modere',
        1.7: 'Actif',
        1.8: 'Tres actif',
        1.9: 'Intense',
        2.0: 'Athlete',
      };
      return napLabels[user.sportProfile.nap] || `NAP ${user.sportProfile.nap}`;
    }
    return '-';
  };

  return (
    <Box>
      {/* Profil Client */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header: Avatar + Nom (masque pour vue client) */}
          {!isClientView && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Avatar
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  bgcolor: primary[500],
                  color: grey[900],
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 600,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography variant="h2">
                  {user.firstName} {user.lastName}
                </Typography>
                {user.email && (
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {/* Chips: Preferences + Intolerances + Objectifs */}
          {(dietaryPreferences?.length > 0 || allergies?.length > 0 || goals) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {dietaryPreferences?.map((pref, index) => (
                <Chip
                  key={index}
                  label={DIETARY_TRANSLATIONS[pref] || pref}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
              {allergies?.map((allergy, index) => (
                <Chip
                  key={`allergy-${index}`}
                  label={INTOLERANCE_TRANSLATIONS[allergy] || allergy}
                  size="small"
                  color="error"
                  variant="outlined"
                />
              ))}
              {goals && (
                <Chip label={goals} size="small" color="success" variant="outlined" />
              )}
            </Box>
          )}

          {/* Stats: Poids / Taille / Age / Activite */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: grey[50],
                borderRadius: 1,
                border: '1px solid',
                borderColor: grey[200],
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.25 }}>
                {user.weight ? `${user.weight} kg` : '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Poids
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: grey[50],
                borderRadius: 1,
                border: '1px solid',
                borderColor: grey[200],
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.25 }}>
                {user.height ? `${user.height} cm` : '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Taille
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: grey[50],
                borderRadius: 1,
                border: '1px solid',
                borderColor: grey[200],
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 600, mb: 0.25 }}>
                {user.age ? `${user.age} ans` : '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Age
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: 'center',
                p: 1.5,
                bgcolor: grey[50],
                borderRadius: 1,
                border: '1px solid',
                borderColor: grey[200],
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  mb: 0.25,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                }}
              >
                {getActivityLabel()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Activite
              </Typography>
            </Box>
          </Box>

          {/* Besoins Energetiques */}
          <EnergyNeeds user={user} />
        </CardContent>
      </Card>

      {/* Section Commentaires (masque pour vue client) */}
      {!isClientView && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Commentaires
            </Typography>
            <Comments planId={planId} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default GeneralInfo;
